import { Button, Drawer } from "@etudescarre/ui";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllYards } from "./components";

type TeamFormData = {
  name: string;
  yardId?: number;
  contractId?: number;
  description: string;
  delaiPourVisaDays: string;
};

type Yard = {
  id: number;
  name: string;
  moaCompanyId: number | null;
  moaCompany: { id: number; name: string } | null;
};

type Contract = {
  id: number;
  performer: { id: number; name: string };
};

type Team = {
  id: number;
  yardId: number;
  contractId: number;
  name: string;
  description: string | null;
  delaiPourVisaDays: number | null;
};

export type TeamFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: Team;
  onSuccess?: () => void;
};

export function TeamForm({
  open,
  onOpenChange,
  team,
  onSuccess,
}: TeamFormProps) {
  const [yards, setYards] = useState<Yard[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loadingYards, setLoadingYards] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const isEdit = !!team;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<TeamFormData>({
    defaultValues: {
      name: team?.name ?? "",
      yardId: team?.yardId,
      contractId: team?.contractId,
      description: team?.description ?? "",
      delaiPourVisaDays: team?.delaiPourVisaDays
        ? String(team.delaiPourVisaDays)
        : "",
    },
  });

  const yardId = watch("yardId");

  const fetchYards = useCallback(async () => {
    setLoadingYards(true);
    const { yards: allYards } = await fetchAllYards();
    if (allYards) {
      setYards(
        allYards.map((y) => ({
          id: y.id,
          name: y.name,
          moaCompanyId: y.moaCompanyId,
          moaCompany: y.moaCompany
            ? { id: y.moaCompany.id, name: y.moaCompany.name }
            : null,
        })),
      );
    }
    setLoadingYards(false);
  }, []);

  const fetchContracts = useCallback(async (selectedYardId: number) => {
    if (!selectedYardId) {
      setContracts([]);
      return;
    }
    setLoadingContracts(true);
    // Fetch teams for the yard to get contracts (via my-contracts or admin endpoint)
    // For now, we use teams list to see what contracts exist for this yard
    // We need to call the yard contracts endpoint
    const { data } = await apiClient.GET("/api/admin/teams", {
      params: { query: { yardId: selectedYardId } },
    });
    if (data) {
      // Extract unique contracts from teams on this yard
      const uniqueContracts = new Map<number, Contract>();
      for (const t of data.data.teams) {
        uniqueContracts.set(t.contract.id, {
          id: t.contract.id,
          performer: t.contract.performer,
        });
      }
      setContracts([...uniqueContracts.values()]);
    }
    setLoadingContracts(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchYards();
      reset({
        name: team?.name ?? "",
        yardId: team?.yardId,
        contractId: team?.contractId,
        description: team?.description ?? "",
        delaiPourVisaDays: team?.delaiPourVisaDays
          ? String(team.delaiPourVisaDays)
          : "",
      });
      if (team?.yardId) {
        fetchContracts(team.yardId);
      }
    }
  }, [open, team, reset, fetchYards, fetchContracts]);

  useEffect(() => {
    if (yardId && yardId > 0) {
      fetchContracts(yardId);
      if (!team || team.yardId !== Number(yardId)) {
        setValue("contractId", undefined);
      }
    } else {
      setContracts([]);
    }
  }, [yardId, team, fetchContracts, setValue]);

  const onSubmit = async (data: TeamFormData) => {
    try {
      if (isEdit) {
        const { error } = await apiClient.PATCH("/api/admin/teams/{id}", {
          params: { path: { id: team.id } },
          body: {
            name: data.name,
            description: data.description || null,
            delaiPourVisaDays: data.delaiPourVisaDays
              ? Number(data.delaiPourVisaDays)
              : null,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la modification de l'équipe",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Équipe modifiée avec succès",
        });
      } else {
        const { error } = await apiClient.POST("/api/admin/teams", {
          body: {
            yardId: Number(data.yardId),
            contractId: Number(data.contractId),
            name: data.name,
            description: data.description || undefined,
            delaiPourVisaDays: data.delaiPourVisaDays
              ? Number(data.delaiPourVisaDays)
              : undefined,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la création de l'équipe",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Équipe créée avec succès",
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>
                  {isEdit ? "Modifier l'équipe" : "Créer une équipe"}
                </Drawer.Title>
                <Drawer.Description>
                  {isEdit
                    ? "Modifiez les informations de l'équipe"
                    : "Créez une nouvelle équipe"}
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* Yard Select (create only) */}
                {!isEdit && (
                  <div>
                    <label
                      htmlFor="yardId"
                      className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                    >
                      Chantier <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="yardId"
                      {...register("yardId", { valueAsNumber: true })}
                      required
                      disabled={loadingYards}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    >
                      <option value="">Sélectionner un chantier</option>
                      {yards.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                          {y.moaCompany ? ` (${y.moaCompany.name})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Contract Select (create only) */}
                {!isEdit && (
                  <div>
                    <label
                      htmlFor="contractId"
                      className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                    >
                      Contrat <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="contractId"
                      {...register("contractId", { valueAsNumber: true })}
                      required
                      disabled={
                        loadingContracts || !yardId || Number(yardId) === 0
                      }
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    >
                      <option value="">
                        {!yardId || Number(yardId) === 0
                          ? "Sélectionnez d'abord un chantier"
                          : contracts.length === 0
                            ? "Aucun contrat disponible"
                            : "Sélectionner un contrat"}
                      </option>
                      {contracts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.performer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...register("name")}
                    required
                    minLength={1}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Nom de l'équipe"
                  />
                </div>

                {/* Description */}
                <div>
                  <label
                    htmlFor="description"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    {...register("description")}
                    rows={3}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Description de l'équipe (optionnel)"
                  />
                </div>

                {/* Délai pour visa */}
                <div>
                  <label
                    htmlFor="delaiPourVisaDays"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Délai pour visa (jours)
                  </label>
                  <input
                    id="delaiPourVisaDays"
                    type="number"
                    {...register("delaiPourVisaDays")}
                    min={0}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Ex: 7"
                  />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : isEdit ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
